import { useEffect, useRef, useState } from 'react';
import { Search, Loader2 } from 'lucide-react';
import organizationService, { type OrgSearchResult } from '../../services/organization';

// Debounced typeahead for picking an organisation by name. Calls
// /organisations/search; results are limited to 50 server-side.

interface Props {
  value: { id: number; name: string } | null;
  onChange: (org: { id: number; name: string } | null) => void;
  placeholder?: string;
  stakeholderType?: string;
}

export default function OrgPicker({ value, onChange, placeholder, stakeholderType }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<OrgSearchResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params: { name?: string; stakeholder_type?: string } = {};
        if (query.trim()) params.name = query.trim();
        if (stakeholderType) params.stakeholder_type = stakeholderType;
        const r = await organizationService.searchOrganizations(params);
        if (r.success && r.data) setResults(r.data);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [query, open, stakeholderType]);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  if (value) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-2 border border-neutral-300 rounded text-sm bg-neutral-50">
        <span className="font-medium">{value.name}</span>
        <span className="text-xs text-neutral-500">#{value.id}</span>
        <button
          type="button"
          className="ml-2 text-neutral-400 hover:text-neutral-700 text-xs"
          onClick={() => onChange(null)}
        >
          change
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-center gap-2 px-2 py-1.5 border border-neutral-300 rounded bg-white">
        <Search className="h-4 w-4 text-neutral-400" />
        <input
          className="flex-1 outline-none text-sm bg-transparent"
          placeholder={placeholder || 'Search organisations…'}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
        />
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-400" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-neutral-200 rounded shadow-lg">
          {results.map(o => (
            <button
              type="button"
              key={o.id}
              className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50"
              onClick={() => { onChange({ id: o.id, name: o.name }); setOpen(false); setQuery(''); }}
            >
              <div className="font-medium">{o.name}</div>
              {o.stakeholder_types && o.stakeholder_types.length > 0 && (
                <div className="text-xs text-neutral-500">{o.stakeholder_types.join(', ')}</div>
              )}
            </button>
          ))}
        </div>
      )}
      {open && !loading && results.length === 0 && query && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-neutral-200 rounded shadow-lg p-3 text-xs text-neutral-500">
          No organisations found.
        </div>
      )}
    </div>
  );
}
